import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Instagram, ShieldCheck, Store, UserRound } from "lucide-react";
import { api, imgUrl, INSTAGRAM_URL, INSTAGRAM_HANDLE } from "../lib/api";
import { useCategories, useSeo } from "../lib/hooks";
import { ProductGrid } from "../components/ProductCard";
import { Reveal, SectionTitle } from "../components/Reveal";

const HERO_IMG = "https://images.unsplash.com/photo-1780811070739-faa3b575b669?crop=entropy&cs=srgb&fm=jpg&w=1800&q=80";

const Hero = () => (
  <section className="relative overflow-hidden grain min-h-[88vh] flex items-center" data-testid="home-hero">
    <img src={HERO_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-35 scale-105" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0B] via-[#0B0B0B]/85 to-[#0B0B0B]/30" />
    <div className="relative z-10 max-w-7xl mx-auto w-full px-5 sm:px-8 py-16 grid lg:grid-cols-12 gap-10 items-center">
      <div className="lg:col-span-7">
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2 }}
          className="h-36 w-36 sm:h-44 sm:w-44 mb-8 overflow-hidden bg-black" data-testid="hero-logo">
          <img src="/logo-nasdeal.jpg" alt="Logo NasDeal" className="h-full w-full object-cover scale-[1.3]" />
        </motion.div>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 1 }} className="eyebrow mb-5">Maison de parfums</motion.p>
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 1 }}
          className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-[#F8F6F1]" data-testid="hero-slogan">
          Le luxe <em className="gold-text not-italic">à portée</em><br />de tous.
        </motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7, duration: 1 }}
          className="mt-6 max-w-md text-sm sm:text-base text-[#F8F6F1]/70 leading-relaxed">
          Des parfums choisis avec exigence, inspirés des grandes maisons, à des prix pensés pour chacun.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }} className="mt-10 flex flex-col sm:flex-row gap-4">
          <Link to="/boutique" className="btn-gold" data-testid="hero-discover-button">Découvrir nos parfums <ArrowRight className="h-4 w-4" /></Link>
          <a href="#collections" className="btn-outline-gold" data-testid="hero-collections-button">Nos collections</a>
        </motion.div>
      </div>
    </div>
  </section>
);

const Presentation = () => (
  <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32 grid lg:grid-cols-12 gap-12 items-center" data-testid="home-presentation">
    <Reveal className="lg:col-span-5 order-2 lg:order-1">
      <div className="relative aspect-[4/5] overflow-hidden">
        <img src="https://images.unsplash.com/photo-1643797519086-cc9a821fbcfe?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80" alt="Flacon de parfum sur étoffe noire" loading="lazy" className="h-full w-full object-cover" />
        <div className="absolute inset-4 border border-[#C9A45C]/40 pointer-events-none" />
      </div>
    </Reveal>
    <Reveal delay={0.15} className="lg:col-span-6 lg:col-start-7 order-1 lg:order-2">
      <p className="eyebrow mb-5">La maison NasDeal</p>
      <h2 className="font-display text-4xl sm:text-5xl leading-tight">L'élégance olfactive, <span className="italic text-[#C9A45C]">sans compromis</span>.</h2>
      <p className="mt-6 text-[#F8F6F1]/70 leading-relaxed">
        NasDeal est une jeune maison française née d'une conviction simple : chacun mérite de porter un parfum qui lui ressemble.
        Nous sélectionnons des fragrances, des brumes, des muscs et des parfums d'intérieur au caractère affirmé, et les proposons à des prix accessibles.
      </p>
      <Link to="/a-propos" className="mt-8 inline-flex items-center gap-2 eyebrow hover:gap-4 transition-all duration-300" data-testid="presentation-about-link">
        Notre histoire <ArrowRight className="h-4 w-4" />
      </Link>
    </Reveal>
  </section>
);

const Collections = () => {
  const cats = useCategories();
  return (
    <section id="collections" className="max-w-7xl mx-auto px-5 sm:px-8 py-16 scroll-mt-28" data-testid="home-collections">
      <SectionTitle eyebrow="Univers" title="Nos collections" />
      <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[200px] sm:auto-rows-[260px] gap-3 sm:gap-4">
        {cats.map((c, i) => (
          <Reveal key={c.slug} delay={i * 0.05} className={i === 0 ? "col-span-2 row-span-2" : i === 3 ? "md:col-span-2" : ""}>
            <Link to={`/categorie/${c.slug}`} className="group relative block h-full overflow-hidden bg-[#141414]" data-testid={`collection-card-${c.slug}`}>
              <img src={imgUrl(c.image)} alt={c.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-70 transition-transform duration-[1400ms] group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B] via-[#0B0B0B]/30 to-transparent" />
              <div className="absolute bottom-0 p-4 sm:p-6">
                <p className="font-display text-xl sm:text-3xl text-[#F8F6F1] group-hover:text-[#E5C178] transition-colors duration-300">{c.name}</p>
                <p className="text-[10px] tracking-[0.25em] uppercase text-[#C9A45C] mt-1">{c.product_count} produit{c.product_count > 1 ? "s" : ""}</p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const ProductSection = ({ eyebrow, title, params, testid, empty }) => {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/products", { params: { ...params, limit: 4 } }).then((r) => setItems(r.data)).catch(() => setItems([])); }, []); // eslint-disable-line
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16" data-testid={testid}>
      <div className="flex items-end justify-between gap-6">
        <SectionTitle eyebrow={eyebrow} title={title} />
        <Link to="/boutique" className="hidden sm:inline-flex mb-14 eyebrow items-center gap-2 hover:gap-4 transition-all">Tout voir <ArrowRight className="h-4 w-4" /></Link>
      </div>
      {items && items.length === 0 ? (
        <p className="border border-dashed border-[#C9A45C]/30 p-8 text-center text-sm text-[#F8F6F1]/55" data-testid={`${testid}-empty`}>{empty}</p>
      ) : <ProductGrid products={items || []} testid={`${testid}-grid`} />}
    </section>
  );
};

const Engagements = () => (
  <section className="border-y border-[#C9A45C]/15 bg-[#0e0e0e]" data-testid="home-engagements">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 grid sm:grid-cols-3 gap-8">
      {[[ShieldCheck, "Paiement sécurisé", "Transactions chiffrées via Stripe"], [Store, "Retrait gratuit", "En main propre, sur rendez-vous"], [UserRound, "Sans compte", "Commandez en quelques instants"]].map(([Icon, t, d]) => (
        <div key={t} className="flex items-center gap-4">
          <span className="h-12 w-12 border border-[#C9A45C]/40 flex items-center justify-center shrink-0"><Icon className="h-5 w-5 text-[#C9A45C]" /></span>
          <div><p className="font-display text-xl">{t}</p><p className="text-xs text-[#F8F6F1]/55">{d}</p></div>
        </div>
      ))}
    </div>
  </section>
);

const InstagramSection = () => {
  const imgs = ["photo-1758871993077-e084cc7eca86", "photo-1630573133526-8d090e0269af", "photo-1738414808975-201966230c59", "photo-1774653256680-9ba4f10ca7c4"];
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20" data-testid="home-instagram-section">
      <div className="relative overflow-hidden border border-[#C9A45C]/25 bg-[#101010] grid lg:grid-cols-2">
        <div className="p-8 sm:p-14 flex flex-col justify-center relative z-10">
          <Instagram className="h-8 w-8 text-[#C9A45C] mb-6" />
          <p className="eyebrow mb-4">{INSTAGRAM_HANDLE}</p>
          <h2 className="font-display text-4xl sm:text-5xl leading-tight">Rejoignez-nous sur Instagram</h2>
          <p className="mt-5 text-[#F8F6F1]/65 leading-relaxed max-w-md">
            Suivez NasDeal pour découvrir en avant-première nos nouveautés, nos arrivages et nos promotions.
          </p>
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="btn-gold mt-8 self-start" data-testid="home-instagram-button">
            <Instagram className="h-4 w-4" /> Suivre {INSTAGRAM_HANDLE}
          </a>
        </div>
        <div className="grid grid-cols-2 gap-1">
          {imgs.map((id) => (
            <a key={id} href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="group relative aspect-square overflow-hidden" aria-label="Voir Instagram NasDeal">
              <img src={`https://images.unsplash.com/${id}?crop=entropy&cs=srgb&fm=jpg&w=600&q=75`} alt="Univers NasDeal" loading="lazy" className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-110" />
              <span className="absolute inset-0 bg-[#0B0B0B]/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"><Instagram className="h-6 w-6 text-[#C9A45C]" /></span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  useSeo({ title: null, description: "NasDeal, parfumerie en ligne : parfums homme, femme, mixtes, parfums de Dubaï, brumes parfumées, parfums d'intérieur et muscs. Le luxe à portée de tous." });
  return (
    <div data-testid="home-page">
      <Hero />
      <Engagements />
      <Presentation />
      <Collections />
      <ProductSection eyebrow="Arrivages" title="Nouveautés" params={{ flag: "new" }} testid="home-new" empty="Les nouveautés arrivent bientôt." />
      <ProductSection eyebrow="Plébiscités" title="Meilleures ventes" params={{ flag: "best" }} testid="home-best"
        empty="Les meilleures ventes s'afficheront automatiquement dès les premières commandes." />
      <ProductSection eyebrow="Sélection" title="Nos coups de cœur" params={{ flag: "featured" }} testid="home-featured" empty="Notre sélection arrive bientôt." />
      <InstagramSection />
    </div>
  );
}
